// v10/src/components/nav/NavigationPlanner.js
//
// R7 Parte 1 — Pantalla full-screen para planificar un vuelo de navegación.
// Pattern como WBCalculator: state machine en App.js, motion.div, sticky header.
//
// Props: { darkMode, currentUser, onClose }

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Plus, X, MapPin, Save, ClipboardList, ExternalLink,
  Loader, AlertCircle, Plane, Compass, Fuel, Clock, Ruler, Wind, CloudSun,
} from 'lucide-react';
import api from '../../services/apiService';
import navigationService from '../../services/navigationService';
import { calculateNavigation } from '../../utils/navigationCalculator';
import NavigationMap from './NavigationMap';
import AirportAutocomplete from './AirportAutocomplete';
import SaveNavModal from './SaveNavModal';
import NavHistoryModal from './NavHistoryModal';

const MAX_WAYPOINTS = 5;

const NavigationPlanner = ({ darkMode, currentUser, onClose }) => {
  const { t } = useTranslation();

  // ── Estilos compartidos ────────────────────────────────────────
  const text   = darkMode ? 'white' : '#0f172a';
  const sub    = darkMode ? 'rgba(255,255,255,0.55)' : '#64748b';
  const cardBg = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)';
  const border = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)';
  const glassBg = darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)';

  // ── State ──────────────────────────────────────────────────────
  const [aircraftList, setAircraftList] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [errorList, setErrorList] = useState(null);

  const [selectedAircraftId, setSelectedAircraftId] = useState('');
  const selectedAircraft = aircraftList.find(a => a.id === selectedAircraftId) || null;

  const [origin, setOrigin] = useState(null);         // {icao, name, lat, lng}
  const [destination, setDestination] = useState(null);
  const [waypoints, setWaypoints] = useState([]);     // array de {icao,name,lat,lng}|null

  const [cruiseTasOverride, setCruiseTasOverride] = useState('');
  const [cruiseGphOverride, setCruiseGphOverride] = useState('');
  const [cruiseAltFt, setCruiseAltFt] = useState('');

  // Viento (R7 P2): manual o cargado de OpenWeatherMap. Vacío = sin viento.
  const [windDirection, setWindDirection] = useState('');
  const [windSpeed, setWindSpeed] = useState('');
  const [windLoading, setWindLoading] = useState(false);
  const [windToast, setWindToast] = useState(null); // { type: 'success'|'error', text }

  const [result, setResult] = useState(null);
  const [errorCalc, setErrorCalc] = useState(null);

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [templateToast, setTemplateToast] = useState(false);
  const [nearestLoading, setNearestLoading] = useState(null); // 'origin' | 'destination' | null
  const [nearestError, setNearestError] = useState(null);

  // ── Cargar aeronaves ───────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadingList(true);
      setErrorList(null);
      try {
        const res = await api.get('/api/aircraft', { params: { is_active: 'true' } });
        if (cancelled) return;
        setAircraftList(res.data?.data || []);
      } catch (err) {
        if (!cancelled) {
          setErrorList(
            err.response?.data?.message ||
              t('wb_error_load_list', { ns: 'admin', defaultValue: 'Error al cargar aeronaves' })
          );
        }
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [t]);

  // ── Limpiar overrides al cambiar aeronave ──────────────────────
  useEffect(() => {
    if (selectedAircraftId) {
      setCruiseTasOverride('');
      setCruiseGphOverride('');
    }
  }, [selectedAircraftId]);

  // ── Cálculo en vivo ────────────────────────────────────────────
  useEffect(() => {
    if (!origin || !destination) {
      setResult(null);
      setErrorCalc(null);
      return;
    }
    const tas = Number(cruiseTasOverride) || Number(selectedAircraft?.cruise_tas_knots);
    const gph = Number(cruiseGphOverride) || Number(selectedAircraft?.cruise_consumption_gph);

    if (!tas || !gph) {
      setErrorCalc(t('nav_aircraft_no_performance', { ns: 'admin' }));
      // Mantenemos `result` previo para que el mapa no parpadee — solo limpio
      // si todavía no había nada.
      if (!result) setResult(null);
      return;
    }

    // Viento opcional (R7 P2): si ambos campos válidos y speed > 0, lo aplicamos.
    const windDirNum = Number(windDirection);
    const windSpdNum = Number(windSpeed);
    const windObj = (
      windDirection !== '' && windSpeed !== '' &&
      Number.isFinite(windDirNum) && Number.isFinite(windSpdNum) &&
      windSpdNum > 0
    )
      ? { direction_deg: windDirNum, speed_kt: windSpdNum }
      : null;

    try {
      const valid = waypoints.filter(w => w && Number.isFinite(w.lat) && Number.isFinite(w.lng));
      const allWps = [origin, ...valid, destination];
      const r = calculateNavigation(
        allWps,
        { tas_knots: tas, consumption_gph: gph },
        { wind: windObj }
      );
      setResult(r);
      setErrorCalc(null);
    } catch (err) {
      setErrorCalc(err.message);
      // Mantengo el último resultado válido — el form sigue editable y el mapa
      // muestra el último cálculo OK en lugar de parpadear.
    }
    // eslint-disable-next-line
  }, [origin, destination, waypoints, selectedAircraft, cruiseTasOverride, cruiseGphOverride, windDirection, windSpeed]);

  // ── Handlers waypoints ─────────────────────────────────────────
  const handleAddWaypoint = () => {
    if (waypoints.length >= MAX_WAYPOINTS) return;
    setWaypoints(prev => [...prev, null]);
  };

  const handleSetWaypoint = (idx, airport) => {
    setWaypoints(prev => {
      const next = [...prev];
      next[idx] = airport;
      return next;
    });
  };

  const handleRemoveWaypoint = (idx) => {
    setWaypoints(prev => prev.filter((_, i) => i !== idx));
  };

  // ── Viento (R7 P2): cargar desde OpenWeatherMap del origen ─────
  const showWindToast = (toast) => {
    setWindToast(toast);
    setTimeout(() => setWindToast(null), 2400);
  };

  const handleLoadWindFromWeather = async () => {
    if (!origin || !Number.isFinite(origin.lat) || !Number.isFinite(origin.lng)) {
      showWindToast({
        type: 'error',
        text: t('nav_wind_no_origin', { ns: 'admin' }),
      });
      return;
    }
    const apiKey = process.env.REACT_APP_OPENWEATHER_KEY;
    if (!apiKey) {
      showWindToast({
        type: 'error',
        text: t('nav_wind_loaded_error', { ns: 'admin' }),
      });
      return;
    }
    setWindLoading(true);
    try {
      const url = `https://api.openweathermap.org/data/2.5/weather?lat=${origin.lat}&lon=${origin.lng}&appid=${apiKey}&units=metric&lang=es`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const deg = data?.wind?.deg;
      const speedMs = data?.wind?.speed;
      if (typeof speedMs !== 'number') throw new Error('Sin datos de viento');
      // OpenWeather devuelve viento en m/s, convertir a knots
      const speedKt = Math.round(speedMs * 1.94384);
      setWindDirection(String(Math.round(deg || 0)));
      setWindSpeed(String(speedKt));
      showWindToast({
        type: 'success',
        text: t('nav_wind_loaded_success', { ns: 'admin' }),
      });
    } catch (err) {
      console.error('Error cargando viento de OpenWeather', err);
      showWindToast({
        type: 'error',
        text: t('nav_wind_loaded_error', { ns: 'admin' }),
      });
    } finally {
      setWindLoading(false);
    }
  };

  const handleClearWind = () => {
    setWindDirection('');
    setWindSpeed('');
  };

  // ── Geolocalización: aeródromo cercano ─────────────────────────
  const handleUseNearestAirport = (target) => {
    setNearestError(null);
    if (!navigator.geolocation) {
      setNearestError(t('nav_geolocation_unavailable', { ns: 'admin' }));
      return;
    }
    setNearestLoading(target);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const r = await navigationService.getNearestAirport(
            pos.coords.latitude,
            pos.coords.longitude,
            50
          );
          if (!r.found || !r.data) {
            setNearestError(t('nav_no_nearby_airport', { ns: 'admin' }));
            return;
          }
          // El endpoint /nearest no devuelve lat/lng — pedimos los completos.
          const full = await navigationService.getAirportByIcao(r.data.icao_code);
          const ap = full.data;
          const wp = {
            icao: ap.icao_code,
            name: ap.name,
            lat: Number(ap.latitude),
            lng: Number(ap.longitude),
          };
          if (target === 'origin') setOrigin(wp);
          else if (target === 'destination') setDestination(wp);
        } catch (err) {
          setNearestError(
            err.response?.data?.message ||
              t('nav_nearest_error', { ns: 'admin' })
          );
        } finally {
          setNearestLoading(null);
        }
      },
      () => {
        setNearestLoading(null);
        setNearestError(t('nav_geolocation_denied', { ns: 'admin' }));
      },
      { enableHighAccuracy: false, timeout: 8000 }
    );
  };

  // ── Cargar como template desde histórico ───────────────────────
  const handleLoadFromHistory = async (item) => {
    if (!item) return;
    try {
      // Resolver los ICAOs a {icao,name,lat,lng}
      const wpIcaos = Array.isArray(item.waypoints) ? item.waypoints : [];
      const icaos = [item.origin_icao, ...wpIcaos, item.destination_icao].filter(Boolean);

      const resolved = await Promise.all(
        icaos.map(async (icao) => {
          try {
            const r = await navigationService.getAirportByIcao(icao);
            const ap = r.data;
            return { icao: ap.icao_code, name: ap.name, lat: Number(ap.latitude), lng: Number(ap.longitude) };
          } catch {
            // Si el aeródromo se borró, mantengo el ICAO con lat/lng vacíos (no romperá el render
            // pero el cálculo lo va a saltar)
            return { icao, name: icao, lat: NaN, lng: NaN };
          }
        })
      );

      const newOrigin = resolved[0];
      const newDestination = resolved[resolved.length - 1];
      const newWaypoints = resolved.slice(1, -1);

      setOrigin(newOrigin);
      setDestination(newDestination);
      setWaypoints(newWaypoints);

      if (item.aircraft_id) setSelectedAircraftId(item.aircraft_id);
      if (item.cruise_tas_knots) setCruiseTasOverride(String(item.cruise_tas_knots));
      if (item.cruise_consumption_gph) setCruiseGphOverride(String(item.cruise_consumption_gph));
      if (item.cruise_altitude_ft) setCruiseAltFt(String(item.cruise_altitude_ft));

      // Restaurar viento del snapshot persistido (R7 P2). Plan viejo sin viento → limpiar.
      const savedWind = item?.result?.wind;
      if (savedWind && Number.isFinite(Number(savedWind.direction_deg)) && Number.isFinite(Number(savedWind.speed_kt))) {
        setWindDirection(String(Math.round(Number(savedWind.direction_deg))));
        setWindSpeed(String(Math.round(Number(savedWind.speed_kt))));
      } else {
        setWindDirection('');
        setWindSpeed('');
      }

      setTemplateToast(true);
      setTimeout(() => setTemplateToast(false), 2200);
    } catch {
      // silent
    }
  };

  // ── Helpers ────────────────────────────────────────────────────
  const effectiveTas = Number(cruiseTasOverride) || Number(selectedAircraft?.cruise_tas_knots) || 0;
  const effectiveGph = Number(cruiseGphOverride) || Number(selectedAircraft?.cruise_consumption_gph) || 0;
  const performanceConfigured = effectiveTas > 0 && effectiveGph > 0;

  const mapWaypoints = [
    origin,
    ...waypoints.filter(Boolean),
    destination,
  ].filter(Boolean);

  const planSnapshot = {
    origin,
    destination,
    waypoints: waypoints.filter(Boolean),
    cruiseTas: cruiseTasOverride ? Number(cruiseTasOverride) : null,
    cruiseGph: cruiseGphOverride ? Number(cruiseGphOverride) : null,
    cruiseAltFt: cruiseAltFt ? Number(cruiseAltFt) : null,
    wind: (
      windDirection !== '' && windSpeed !== '' &&
      Number.isFinite(Number(windDirection)) && Number.isFinite(Number(windSpeed)) &&
      Number(windSpeed) > 0
    )
      ? { direction_deg: Number(windDirection), speed_kt: Number(windSpeed) }
      : null,
  };

  const aircraftLabel = selectedAircraft
    ? [
        selectedAircraft.registration,
        [selectedAircraft.manufacturer, selectedAircraft.model_name].filter(Boolean).join(' '),
      ].filter(Boolean).join(' — ')
    : '';

  const canSave = !!(
    selectedAircraftId && origin && destination && result && performanceConfigured
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 40 }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: darkMode ? '#0a0f1e' : '#f1f5f9',
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          background: darkMode ? 'rgba(10,15,30,0.98)' : 'rgba(241,245,249,0.98)',
          backdropFilter: 'blur(20px)',
          borderBottom: border,
          padding: '14px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
        }}
      >
        <motion.button
          onClick={onClose}
          whileTap={{ scale: 0.92 }}
          style={{
            width: 40, height: 40, borderRadius: 12,
            border, background: glassBg,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: sub, backdropFilter: 'blur(8px)',
          }}
        >
          <ArrowLeft style={{ width: 18, height: 18 }} />
        </motion.button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 32, height: 32, borderRadius: 10,
                background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 3px 12px rgba(99,102,241,0.35)',
                flexShrink: 0,
              }}
            >
              <Compass style={{ width: 16, height: 16, color: 'white' }} />
            </div>
            <p style={{ margin: 0, fontSize: 18, fontWeight: 800, color: text }}>
              {t('nav_planner_title', { ns: 'admin' })}
            </p>
          </div>
        </div>

        {/* Botones header */}
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <motion.button
            onClick={() => setShowHistoryModal(true)}
            whileTap={{ scale: 0.94 }}
            title={t('nav_view_history', { ns: 'admin' })}
            style={headerBtnStyle(darkMode, border, glassBg, text)}
          >
            <ClipboardList style={{ width: 14, height: 14 }} />
            <span className="nav-btn-label">{t('nav_view_history', { ns: 'admin' })}</span>
          </motion.button>
          <motion.button
            onClick={() => window.open('https://yovuelo.com.ar/fpl.php', '_blank')}
            whileTap={{ scale: 0.94 }}
            title={t('nav_open_yovuelo', { ns: 'admin' })}
            style={headerBtnStyle(darkMode, border, glassBg, text)}
          >
            <ExternalLink style={{ width: 14, height: 14 }} />
            <span className="nav-btn-label">{t('nav_open_yovuelo', { ns: 'admin' })}</span>
          </motion.button>
          <motion.button
            onClick={() => setShowSaveModal(true)}
            disabled={!canSave}
            whileTap={canSave ? { scale: 0.94 } : undefined}
            title={t('nav_save_button', { ns: 'admin' })}
            style={{
              padding: '8px 12px',
              borderRadius: 10,
              border: 'none',
              background: canSave
                ? 'linear-gradient(135deg,#6366f1,#8b5cf6)'
                : (darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.08)'),
              color: canSave ? 'white' : sub,
              cursor: canSave ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              fontWeight: 700,
              boxShadow: canSave ? '0 4px 14px rgba(99,102,241,0.35)' : 'none',
            }}
          >
            <Save style={{ width: 14, height: 14 }} />
            <span className="nav-btn-label">{t('nav_save_button', { ns: 'admin' })}</span>
          </motion.button>
        </div>
      </div>

      <div style={{ maxWidth: 880, margin: '0 auto', padding: '20px 16px 40px' }}>

        {/* Disclaimer referencial — no descartable */}
        <div
          role="note"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            background: darkMode ? 'rgba(245,158,11,0.10)' : 'rgba(245,158,11,0.12)',
            border: '1px solid rgba(245,158,11,0.4)',
            borderRadius: 12,
            padding: '12px 14px',
            marginBottom: 16,
            color: darkMode ? '#fde68a' : '#92400e',
            fontSize: 12.5,
            lineHeight: 1.45,
            fontWeight: 500,
          }}
        >
          <span style={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }} aria-hidden="true">⚠️</span>
          <span>{t('nav_disclaimer_referential', { ns: 'admin' })}</span>
        </div>

        {/* Selector aeronave */}
        <Card cardBg={cardBg} border={border}>
          <Label sub={sub}>
            <Plane style={{ width: 12, height: 12, display: 'inline', marginRight: 6, verticalAlign: -1 }} />
            {t('nav_select_aircraft', { ns: 'admin' })}
          </Label>
          {loadingList ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: sub, fontSize: 13 }}>
              <Loader style={{ width: 14, height: 14 }} className="nav-spin" />
            </div>
          ) : errorList ? (
            <div style={{ color: '#ef4444', fontSize: 13 }}>{errorList}</div>
          ) : aircraftList.length === 0 ? (
            <div style={{ color: sub, fontSize: 13 }}>
              {t('wb_no_aircraft', { ns: 'admin', defaultValue: 'Sin aeronaves' })}
            </div>
          ) : (
            <select
              value={selectedAircraftId}
              onChange={(e) => setSelectedAircraftId(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 10,
                border,
                background: darkMode ? 'rgba(255,255,255,0.05)' : 'white',
                color: text,
                fontSize: 14,
                outline: 'none',
              }}
            >
              <option value="">—</option>
              {aircraftList.map(a => (
                <option key={a.id} value={a.id}>
                  {[a.registration, [a.manufacturer, a.model_name].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}
                </option>
              ))}
            </select>
          )}

          {/* Aviso si la aeronave no tiene performance configurado */}
          {selectedAircraft && !performanceConfigured && (
            <div
              style={{
                marginTop: 10,
                padding: '10px 12px',
                borderRadius: 10,
                background: darkMode ? 'rgba(245,158,11,0.10)' : 'rgba(254,243,199,0.6)',
                border: '1px solid rgba(245,158,11,0.25)',
                color: darkMode ? '#fbbf24' : '#92400e',
                fontSize: 12,
                lineHeight: 1.4,
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
              }}
            >
              <AlertCircle style={{ width: 14, height: 14, flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontWeight: 700 }}>
                  {t('nav_aircraft_no_performance', { ns: 'admin' })}
                </div>
                <div style={{ opacity: 0.85, marginTop: 2 }}>
                  {t('nav_aircraft_no_performance_help', { ns: 'admin' })}
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Ruta */}
        <Card cardBg={cardBg} border={border}>
          <Label sub={sub}>
            <MapPin style={{ width: 12, height: 12, display: 'inline', marginRight: 6, verticalAlign: -1 }} />
            {t('nav_section_route', { ns: 'admin' })}
          </Label>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Origen */}
            <RouteRow
              label={t('nav_origin', { ns: 'admin' })}
              icon="✈️"
              onUseNearest={() => handleUseNearestAirport('origin')}
              nearestLoading={nearestLoading === 'origin'}
              nearestLabel={t('nav_use_nearest', { ns: 'admin' })}
              darkMode={darkMode}
              sub={sub}
              border={border}
            >
              <AirportAutocomplete
                value={origin}
                onChange={setOrigin}
                placeholder="SAVY, Madryn, ..."
                darkMode={darkMode}
              />
            </RouteRow>

            {/* Waypoints intermedios */}
            {waypoints.map((wp, idx) => (
              <RouteRow
                key={`wp-${idx}`}
                label={`${t('nav_waypoints', { ns: 'admin' })} ${idx + 1}`}
                icon="📍"
                onRemove={() => handleRemoveWaypoint(idx)}
                darkMode={darkMode}
                sub={sub}
                border={border}
              >
                <AirportAutocomplete
                  value={wp}
                  onChange={(ap) => handleSetWaypoint(idx, ap)}
                  placeholder="SAVE, ..."
                  darkMode={darkMode}
                />
              </RouteRow>
            ))}

            {waypoints.length < MAX_WAYPOINTS && (
              <button
                type="button"
                onClick={handleAddWaypoint}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  padding: '8px 12px',
                  borderRadius: 10,
                  border: darkMode ? '1px dashed rgba(255,255,255,0.18)' : '1px dashed rgba(0,0,0,0.18)',
                  background: 'transparent',
                  color: sub,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                <Plus style={{ width: 14, height: 14 }} />
                {t('nav_add_waypoint', { ns: 'admin' })}
              </button>
            )}
            {waypoints.length >= MAX_WAYPOINTS && (
              <div style={{ fontSize: 11, color: sub, textAlign: 'center' }}>
                {t('nav_max_waypoints', { ns: 'admin' })}
              </div>
            )}

            {/* Destino */}
            <RouteRow
              label={t('nav_destination', { ns: 'admin' })}
              icon="🎯"
              onUseNearest={() => handleUseNearestAirport('destination')}
              nearestLoading={nearestLoading === 'destination'}
              nearestLabel={t('nav_use_nearest', { ns: 'admin' })}
              darkMode={darkMode}
              sub={sub}
              border={border}
            >
              <AirportAutocomplete
                value={destination}
                onChange={setDestination}
                placeholder="SARA, ..."
                darkMode={darkMode}
              />
            </RouteRow>

            {nearestError && (
              <div
                style={{
                  padding: '8px 10px',
                  borderRadius: 8,
                  background: 'rgba(239,68,68,0.08)',
                  border: '1px solid rgba(239,68,68,0.2)',
                  color: '#ef4444',
                  fontSize: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <AlertCircle style={{ width: 12, height: 12 }} />
                {nearestError}
              </div>
            )}
          </div>
        </Card>

        {/* Performance overrides */}
        {selectedAircraft && (
          <Card cardBg={cardBg} border={border}>
            <Label sub={sub}>
              <Compass style={{ width: 12, height: 12, display: 'inline', marginRight: 6, verticalAlign: -1 }} />
              {t('nav_section_performance', { ns: 'admin' })}
            </Label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
              <PerfField
                label={t('nav_cruise_tas', { ns: 'admin' })}
                placeholder={
                  selectedAircraft.cruise_tas_knots
                    ? `${selectedAircraft.cruise_tas_knots} ${t('nav_unit_kt', { ns: 'admin' })} · ${t('nav_using_default', { ns: 'admin' })}`
                    : '—'
                }
                value={cruiseTasOverride}
                onChange={setCruiseTasOverride}
                darkMode={darkMode}
                border={border}
                text={text}
                sub={sub}
              />
              <PerfField
                label={t('nav_cruise_consumption', { ns: 'admin' })}
                placeholder={
                  selectedAircraft.cruise_consumption_gph
                    ? `${selectedAircraft.cruise_consumption_gph} gal/h · ${t('nav_using_default', { ns: 'admin' })}`
                    : '—'
                }
                value={cruiseGphOverride}
                onChange={setCruiseGphOverride}
                darkMode={darkMode}
                border={border}
                text={text}
                sub={sub}
              />
              <PerfField
                label={t('nav_cruise_altitude', { ns: 'admin' })}
                placeholder="3000, 5000, ..."
                value={cruiseAltFt}
                onChange={setCruiseAltFt}
                darkMode={darkMode}
                border={border}
                text={text}
                sub={sub}
              />
            </div>
          </Card>
        )}

        {/* Viento (R7 P2) */}
        <Card cardBg={cardBg} border={border}>
          <Label sub={sub}>
            <Wind style={{ width: 12, height: 12, display: 'inline', marginRight: 6, verticalAlign: -1 }} />
            {t('nav_wind_section', { ns: 'admin' })}
          </Label>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 10,
              marginBottom: 10,
            }}
          >
            <PerfField
              label={`${t('nav_wind_direction_placeholder', { ns: 'admin' })} (°)`}
              placeholder="0 — 360"
              value={windDirection}
              onChange={setWindDirection}
              darkMode={darkMode}
              border={border}
              text={text}
              sub={sub}
            />
            <PerfField
              label={`${t('nav_wind_speed_placeholder', { ns: 'admin' })} (kt)`}
              placeholder="0"
              value={windSpeed}
              onChange={setWindSpeed}
              darkMode={darkMode}
              border={border}
              text={text}
              sub={sub}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleLoadWindFromWeather}
              disabled={!origin || windLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 12px',
                borderRadius: 10,
                border,
                background: darkMode ? 'rgba(99,102,241,0.10)' : 'rgba(99,102,241,0.08)',
                color: !origin || windLoading ? sub : (darkMode ? '#a5b4fc' : '#6366f1'),
                cursor: !origin || windLoading ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
                fontSize: 12,
                fontWeight: 700,
                opacity: !origin || windLoading ? 0.6 : 1,
              }}
            >
              {windLoading
                ? <Loader style={{ width: 12, height: 12 }} className="nav-spin" />
                : <CloudSun style={{ width: 12, height: 12 }} />}
              {t('nav_wind_load_auto', { ns: 'admin' })}
            </button>
            <button
              type="button"
              onClick={handleClearWind}
              disabled={windDirection === '' && windSpeed === ''}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 12px',
                borderRadius: 10,
                border,
                background: 'transparent',
                color: sub,
                cursor: (windDirection === '' && windSpeed === '') ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
                fontSize: 12,
                fontWeight: 700,
                opacity: (windDirection === '' && windSpeed === '') ? 0.5 : 1,
              }}
            >
              <X style={{ width: 12, height: 12 }} />
              {t('nav_wind_clear', { ns: 'admin' })}
            </button>
          </div>
        </Card>

        {/* Mapa */}
        <Card cardBg={cardBg} border={border}>
          <NavigationMap waypoints={mapWaypoints} darkMode={darkMode} height="380px" />
        </Card>

        {/* Resultado */}
        {result && (
          <>
            {/* KPIs */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 12,
                marginBottom: 14,
              }}
            >
              <KpiCard
                icon={<Ruler style={{ width: 18, height: 18, color: '#6366f1' }} />}
                label={t('nav_kpi_total_distance', { ns: 'admin' })}
                value={`${result.total_distance_nm} ${t('nav_unit_nm', { ns: 'admin' })}`}
                cardBg={cardBg}
                border={border}
                text={text}
                sub={sub}
              />
              <KpiCard
                icon={<Clock style={{ width: 18, height: 18, color: '#6366f1' }} />}
                label={t('nav_kpi_total_time', { ns: 'admin' })}
                value={`${result.total_time_min} ${t('nav_unit_min', { ns: 'admin' })}`}
                cardBg={cardBg}
                border={border}
                text={text}
                sub={sub}
              />
              <KpiCard
                icon={<Fuel style={{ width: 18, height: 18, color: '#6366f1' }} />}
                label={t('nav_kpi_fuel_flight', { ns: 'admin' })}
                value={`${result.total_fuel_gal} ${t('nav_unit_gal', { ns: 'admin' })}`}
                cardBg={cardBg}
                border={border}
                text={text}
                sub={sub}
              />
              <KpiCard
                icon={<Fuel style={{ width: 18, height: 18, color: '#6366f1' }} />}
                label={t('nav_kpi_fuel_reserve', { ns: 'admin' })}
                value={`${result.reserve_fuel_gal ?? '—'} ${t('nav_unit_gal', { ns: 'admin' })}`}
                hint={t('nav_reserve_hint', { ns: 'admin' })}
                cardBg={cardBg}
                border={border}
                text={text}
                sub={sub}
              />
              <KpiCard
                icon={<Fuel style={{ width: 18, height: 18, color: '#16a34a' }} />}
                label={t('nav_kpi_fuel_total_tank', { ns: 'admin' })}
                value={`${result.total_fuel_with_reserve_gal ?? '—'} ${t('nav_unit_gal', { ns: 'admin' })}`}
                cardBg={cardBg}
                border={border}
                text={text}
                sub={sub}
                highlight
                darkMode={darkMode}
              />
            </div>

            {/* Tabla legs */}
            <Card cardBg={cardBg} border={border}>
              <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 800, color: text }}>
                {t('nav_legs_title', { ns: 'admin' })}
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ color: sub, textAlign: 'left', borderBottom: border }}>
                      <Th>{t('nav_legs_col_from', { ns: 'admin' })}</Th>
                      <Th>{t('nav_legs_col_to', { ns: 'admin' })}</Th>
                      <Th align="right">{t('nav_legs_col_distance', { ns: 'admin' })}</Th>
                      <Th align="right">{t('nav_legs_col_course', { ns: 'admin' })}</Th>
                      {result.wind && (
                        <>
                          <Th align="right">{t('nav_legs_col_heading', { ns: 'admin' })}</Th>
                          <Th align="right">{t('nav_legs_col_gs', { ns: 'admin' })}</Th>
                        </>
                      )}
                      <Th align="right">{t('nav_legs_col_time', { ns: 'admin' })}</Th>
                      <Th align="right">{t('nav_legs_col_fuel', { ns: 'admin' })}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.legs.map((leg, idx) => (
                      <tr
                        key={idx}
                        style={{ borderBottom: idx < result.legs.length - 1 ? border : 'none' }}
                      >
                        <Td text={text}><strong>{leg.from_icao}</strong></Td>
                        <Td text={text}><strong>{leg.to_icao}</strong></Td>
                        <Td text={text} align="right">{leg.distance_nm} {t('nav_unit_nm', { ns: 'admin' })}</Td>
                        <Td text={text} align="right">{leg.true_course_deg}{t('nav_unit_deg', { ns: 'admin' })}</Td>
                        {result.wind && (
                          <>
                            <Td text={text} align="right">
                              {leg.heading_deg}{t('nav_unit_deg', { ns: 'admin' })}
                              {Number.isFinite(Number(leg.wca_deg)) && Number(leg.wca_deg) !== 0 && (
                                <small style={{ marginLeft: 4, opacity: 0.6 }}>
                                  ({leg.wca_deg >= 0 ? '+' : ''}{leg.wca_deg}{t('nav_unit_deg', { ns: 'admin' })})
                                </small>
                              )}
                            </Td>
                            <Td text={text} align="right">
                              {leg.ground_speed_kt} {t('nav_unit_kt', { ns: 'admin' })}
                            </Td>
                          </>
                        )}
                        <Td text={text} align="right">{leg.time_min} {t('nav_unit_min', { ns: 'admin' })}</Td>
                        <Td text={text} align="right">{leg.fuel_gal} {t('nav_unit_gal', { ns: 'admin' })}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}

        {/* Error de cálculo */}
        {errorCalc && (
          <div
            style={{
              padding: '10px 12px',
              borderRadius: 10,
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.2)',
              color: '#ef4444',
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <AlertCircle style={{ width: 14, height: 14 }} />
            {errorCalc}
          </div>
        )}
      </div>

      {/* Modal Guardar */}
      <AnimatePresence>
        {showSaveModal && selectedAircraftId && (
          <SaveNavModal
            aircraftId={selectedAircraftId}
            aircraftLabel={aircraftLabel}
            planSnapshot={planSnapshot}
            currentResult={result}
            onClose={() => setShowSaveModal(false)}
            onSaved={() => setShowSaveModal(false)}
            darkMode={darkMode}
          />
        )}
      </AnimatePresence>

      {/* Modal Histórico */}
      <AnimatePresence>
        {showHistoryModal && (
          <NavHistoryModal
            aircraftId={selectedAircraftId || undefined}
            pilotId={currentUser?.id}
            onClose={() => setShowHistoryModal(false)}
            onLoadAsTemplate={handleLoadFromHistory}
            darkMode={darkMode}
          />
        )}
      </AnimatePresence>

      {/* Toast template */}
      <AnimatePresence>
        {templateToast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            style={{
              position: 'fixed',
              bottom: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 120,
              padding: '10px 16px',
              borderRadius: 12,
              background: 'rgba(34,197,94,0.95)',
              color: 'white',
              fontSize: 13,
              fontWeight: 700,
              boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            }}
          >
            ✓ {t('nav_history_use_as_template', { ns: 'admin' })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast viento (R7 P2) */}
      <AnimatePresence>
        {windToast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            style={{
              position: 'fixed',
              bottom: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 120,
              padding: '10px 16px',
              borderRadius: 12,
              background: windToast.type === 'success'
                ? 'rgba(34,197,94,0.95)'
                : 'rgba(239,68,68,0.95)',
              color: 'white',
              fontSize: 13,
              fontWeight: 700,
              boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            }}
          >
            {windToast.type === 'success' ? '✓' : '!'} {windToast.text}
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @keyframes navSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .nav-spin { animation: navSpin 1s linear infinite; }
        @media (max-width: 540px) {
          .nav-btn-label { display: none; }
        }
      `}</style>
    </motion.div>
  );
};

// ── Subcomponentes ──────────────────────────────────────────────
const Card = ({ children, cardBg, border }) => (
  <div
    style={{
      background: cardBg,
      border,
      borderRadius: 16,
      padding: 16,
      marginBottom: 14,
    }}
  >
    {children}
  </div>
);

const Label = ({ children, sub }) => (
  <label
    style={{
      display: 'block',
      fontSize: 11,
      fontWeight: 700,
      color: sub,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      marginBottom: 10,
    }}
  >
    {children}
  </label>
);

const RouteRow = ({
  label, icon, children, onRemove, onUseNearest, nearestLoading, nearestLabel,
  darkMode, sub, border,
}) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      padding: 10,
      borderRadius: 12,
      border,
      background: darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(15,23,42,0.02)',
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
      }}
    >
      <span style={{ fontSize: 11, fontWeight: 800, color: sub, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        <span style={{ marginRight: 6 }}>{icon}</span>{label}
      </span>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        {onUseNearest && (
          <button
            type="button"
            onClick={onUseNearest}
            disabled={nearestLoading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '4px 10px',
              borderRadius: 8,
              border,
              background: 'transparent',
              cursor: nearestLoading ? 'wait' : 'pointer',
              fontFamily: 'inherit',
              fontSize: 11,
              fontWeight: 700,
              color: sub,
            }}
          >
            {nearestLoading
              ? <Loader style={{ width: 12, height: 12 }} className="nav-spin" />
              : <MapPin style={{ width: 12, height: 12 }} />}
            {nearestLabel}
          </button>
        )}
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label="Eliminar"
            style={{
              border: 'none',
              background: 'transparent',
              color: sub,
              cursor: 'pointer',
              padding: 4,
              borderRadius: 8,
            }}
          >
            <X style={{ width: 14, height: 14 }} />
          </button>
        )}
      </div>
    </div>
    {children}
  </div>
);

const PerfField = ({ label, placeholder, value, onChange, darkMode, border, text, sub }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    <span style={{ fontSize: 11, fontWeight: 700, color: sub }}>{label}</span>
    <input
      type="number"
      inputMode="decimal"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        padding: '9px 11px',
        borderRadius: 10,
        border,
        background: darkMode ? 'rgba(255,255,255,0.05)' : 'white',
        color: text,
        fontSize: 13,
        outline: 'none',
        fontFamily: 'inherit',
        fontVariantNumeric: 'tabular-nums',
      }}
    />
  </label>
);

const KpiCard = ({ icon, label, value, hint, highlight, darkMode, cardBg, border, text, sub }) => {
  const accent = '#16a34a';
  const wrapStyle = highlight
    ? {
        background: darkMode ? 'rgba(22,163,74,0.12)' : 'rgba(22,163,74,0.08)',
        border: '1px solid rgba(22,163,74,0.45)',
        borderRadius: 14,
        padding: 14,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        boxShadow: '0 4px 14px rgba(22,163,74,0.18)',
      }
    : {
        background: cardBg,
        border,
        borderRadius: 14,
        padding: 14,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      };
  const iconBg = highlight ? 'rgba(22,163,74,0.18)' : 'rgba(99,102,241,0.12)';
  const valueColor = highlight ? accent : text;
  const valueSize = highlight ? 18 : 16;
  return (
    <div style={wrapStyle}>
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 10, color: sub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {label}
        </div>
        <div style={{ fontSize: valueSize, fontWeight: 800, color: valueColor, lineHeight: 1.2 }}>
          {value}
        </div>
        {hint && (
          <div style={{ fontSize: 10, color: sub, marginTop: 2, fontWeight: 600 }}>
            {hint}
          </div>
        )}
      </div>
    </div>
  );
};

const Th = ({ children, align }) => (
  <th
    style={{
      padding: '8px 10px',
      fontSize: 10,
      fontWeight: 800,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      textAlign: align || 'left',
    }}
  >
    {children}
  </th>
);

const Td = ({ children, text, align }) => (
  <td
    style={{
      padding: '9px 10px',
      color: text,
      fontVariantNumeric: 'tabular-nums',
      textAlign: align || 'left',
    }}
  >
    {children}
  </td>
);

function headerBtnStyle(darkMode, border, glassBg, text) {
  return {
    padding: '8px 12px',
    borderRadius: 10,
    border,
    background: glassBg,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    color: text,
    fontSize: 12,
    fontWeight: 700,
    backdropFilter: 'blur(8px)',
  };
}

export default NavigationPlanner;
